import React from 'react'
import { Progress, Typography, Space, Alert } from 'antd'
import {
  CheckCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import { FileUploadProgress, FileUploadStatus } from '../types'
import { formatFileSize } from '../utils/file-upload-api'

const { Text } = Typography

interface UploadProgressProps {
  progress: FileUploadProgress
  showDetails?: boolean
  compact?: boolean
  onRetry?: () => void
  onCancel?: () => void
}

const ProgressContainer = styled.div<{ compact?: boolean }>`
  padding: ${props => props.compact ? '8px' : '16px'};
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background-color: #fafafa;
  margin-bottom: 8px;

  &.uploading {
    border-color: #1890ff;
    background-color: #f0f8ff;
  }

  &.complete {
    border-color: #52c41a;
    background-color: #f6ffed;
  }

  &.error {
    border-color: #ff4d4f;
    background-color: #fff2f0;
  }
`

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
`

const FileName = styled(Text)`
  font-weight: 500;
  flex: 1;
`

const FileSize = styled(Text)`
  color: #8c8c8c;
  font-size: 12px;
`

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
`

const ProgressDetails = styled.div`
  margin-top: 8px;
`

const StatusText = styled(Text)<{ status: string }>`
  font-size: 12px;
  color: ${props => {
    switch (props.status) {
      case 'COMPLETE': return '#52c41a'
      case 'ERROR': return '#ff4d4f'
      case 'UPLOADING': return '#1890ff'
      default: return '#8c8c8c'
    }
  }};
`

/**
 * Component to display upload progress for a single file
 */
export const UploadProgress: React.FC<UploadProgressProps> = ({
  progress,
  showDetails = true,
  compact = false,
  onRetry,
  onCancel: _onCancel,
}) => {
  const getStatusIcon = () => {
    switch (progress.status) {
      case FileUploadStatus.COMPLETE:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case FileUploadStatus.ERROR:
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
      case FileUploadStatus.UPLOADING:
      case FileUploadStatus.PROCESSING:
      case FileUploadStatus.GENERATING_TICKET:
        return <LoadingOutlined style={{ color: '#1890ff' }} />
      default:
        return <FileTextOutlined style={{ color: '#8c8c8c' }} />
    }
  }

  const getStatusText = () => {
    switch (progress.status) {
      case FileUploadStatus.IDLE:
        return 'En attente'
      case FileUploadStatus.VALIDATING:
        return 'Validation du fichier...'
      case FileUploadStatus.GENERATING_TICKET:
        return 'Préparation du téléchargement...'
      case FileUploadStatus.UPLOADING:
        return 'Téléchargement en cours...'
      case FileUploadStatus.PROCESSING:
        return 'Traitement du fichier...'
      case FileUploadStatus.COMPLETE:
        return 'Téléchargement terminé'
      case FileUploadStatus.ERROR:
        return 'Erreur de téléchargement'
      default:
        return 'Statut inconnu'
    }
  }

  const getProgressStatus = () => {
    switch (progress.status) {
      case FileUploadStatus.COMPLETE:
        return 'success'
      case FileUploadStatus.ERROR:
        return 'exception'
      case FileUploadStatus.UPLOADING:
      case FileUploadStatus.PROCESSING:
      case FileUploadStatus.GENERATING_TICKET:
        return 'active'
      default:
        return 'normal'
    }
  }

  const getContainerClass = () => {
    switch (progress.status) {
      case FileUploadStatus.COMPLETE:
        return 'complete'
      case FileUploadStatus.ERROR:
        return 'error'
      case FileUploadStatus.UPLOADING:
      case FileUploadStatus.PROCESSING:
      case FileUploadStatus.GENERATING_TICKET:
        return 'uploading'
      default:
        return ''
    }
  }

  const formatProgress = () => {
    if (progress.uploadedSize && progress.totalSize) {
      return `${formatFileSize(progress.uploadedSize)} / ${formatFileSize(progress.totalSize)}`
    }
    if (progress.totalSize) {
      return formatFileSize(progress.totalSize)
    }
    return ''
  }

  return (
    <ProgressContainer compact={compact} className={getContainerClass()}>
      <FileInfo>
        <StatusIcon>{getStatusIcon()}</StatusIcon>
        <FileName ellipsis={{ tooltip: progress.filename }}>{progress.filename}</FileName>
        {progress.totalSize && (
          <FileSize>{formatFileSize(progress.totalSize)}</FileSize>
        )}
      </FileInfo>

      <Progress
        percent={Math.round(progress.progress)}
        status={getProgressStatus()}
        showInfo={!compact}
        strokeWidth={compact ? 4 : 6}
      />

      {showDetails && !compact && (
        <ProgressDetails>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <StatusText status={progress.status}>
              {getStatusText()}
            </StatusText>

            {progress.uploadedSize && progress.totalSize && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {formatProgress()}
              </Text>
            )}

            {progress.error && (
              <Alert
                message="Erreur de téléchargement"
                description={progress.error}
                type="error"
                showIcon
                action={
                  onRetry && (
                    <Space>
                      <Text
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={onRetry}
                      >
                        Réessayer
                      </Text>
                    </Space>
                  )
                }
              />
            )}
          </Space>
        </ProgressDetails>
      )}
    </ProgressContainer>
  )
}

/**
 * Component to display multiple upload progress items
 */
interface MultipleUploadProgressProps {
  progressItems: Record<string, FileUploadProgress>
  showDetails?: boolean
  compact?: boolean
  onRetry?: (fileId: string) => void
  onCancel?: (fileId: string) => void
}

export const MultipleUploadProgress: React.FC<MultipleUploadProgressProps> = ({
  progressItems,
  showDetails = true,
  compact = false,
  onRetry,
  onCancel,
}) => {
  const progressArray = Object.values(progressItems)

  if (progressArray.length === 0) {
    return null
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {progressArray.map((progress) => (
        <UploadProgress
          key={progress.fileId}
          progress={progress}
          showDetails={showDetails}
          compact={compact}
          onRetry={onRetry ? () => onRetry(progress.fileId) : undefined}
          onCancel={onCancel ? () => onCancel(progress.fileId) : undefined}
        />
      ))}
    </Space>
  )
}
